import Cookies from 'js-cookie';
import { pubSub } from '@utils/PubSub';
import { fetchStorefront, clean } from '@utils/GraphQLClient';
import { loadCart, closeCart } from '@utils/global-functions';
import { getDYOptions } from '@utils/dyResources';
import { UPSELL_ITEM, PRODUCT_RESPONSE_FROM_HANDLE } from './Queries';

const headers = async (handle, query) => {
  return {
    variables: {
      handle,
    },
    query,
  };
};

export const actions = {
  fetchWindow() {
    const global = window?.Brooklinen?.promo ?? {};
    this.swd = global?.discounts?.swd ?? false;
    this.tiers = global?.discounts?.tiers?.enabled
      ? global?.discounts?.tiers
      : false;
    this.gwp = global?.gwp ?? false;
    this.mode = window?.Brooklinen?.mode ?? 'production';
    this.swd_tags = global?.discounts?.swd?.tags.split(',') ?? [];
    this.tieredDiscount = global?.discounts?.tiers ?? this.tieredDiscount;
    this.global = global;
    if (this.gwp) {
      this.gwp.active = false;
      this.gwp.added = false;
    }
    this.promoReady = this.swd !== false;
  },
  async fetchEndpoint() {
    const response = await fetch('/cart?view=endpoint');
    return response.json();
  },
  /**
   * Fetches upsell product data. If `handles` is provided, resolves those handles directly
   * against the Shopify Storefront API. Otherwise, fetches recommendations from Dynamic Yield.
   * @param {string[]} [handles] - Optional array of product handles to fetch directly.
   * @returns {Promise<Object[]>} Array of product objects ready for the Upsell component.
   */
  async fetchUpsellItems(handles) {
    if (handles?.length) {
      const items = await Promise.all(
        handles.map(async (handle) => {
          if (!handle) return null;
          const request = await headers(handle.trim(), UPSELL_ITEM);
          const response = await fetchStorefront(request);
          const product = clean(response?.data?.product);
          return product ?? null;
        })
      );
      return items.filter(Boolean);
    }

    const options = await getDYOptions();
    const res = await fetch(
      'https://direct.dy-api.com/v2/serve/user/choose',
      options
    );
    const data = await res.json();

    const choice = data?.choices?.find((c) => c.name === 'You May Also Like');
    const payload = choice?.variations?.[0]?.payload?.data;
    const slots = payload?.slots ?? [];

    this.upsell.decisionId = choice?.decisionId ?? null;
    this.upsell.variationId = choice?.variations?.[0]?.id ?? null;

    const items = await Promise.all(
      slots.map(async (slot) => {
        const handle = slot?.productData?.group_id;
        if (!handle) return null;
        const request = await headers(handle, UPSELL_ITEM);
        const response = await fetchStorefront(request);
        const product = clean(response?.data?.product);
        if (!product) return null;
        product.dySlotId = slot?.slotId ?? null;
        return product;
      })
    );

    this.upsell.title = payload?.custom?.title ?? null;
    return items.filter(Boolean);
  },
  async setAttributes() {
    const attributes = new FormData();
    attributes.append('attributes[online]', 'true');

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      body: attributes,
    });

    return response.json();
  },
  async onChange() {
    try {
      await this.setCartAbandonment();
    } catch (err) {
      throw new Error(`Cart Abandonment Failed: ${err}`);
    }

    try {
      await this.setCartCheckoutUrl();
    } catch (err) {
      throw new Error(`Checkout URL Failed: ${err}`);
    }
  },
  async setCartAbandonment() {
    const items = this.items;
    const hasCookie = Cookies.get('_bl_prod__checkout-token') ?? false;

    if (items.length <= 0 || hasCookie) return;

    const response = await fetch('/cart.json');
    const data = await response.json();
    const token = data.token;

    Cookies.set('_bl_prod__checkout-token', token, { expires: 21 });
    return;
  },
  async setCartCheckoutUrl() {
    const items = this.items;
    const hasInstance = Cookies.get('_bl_prod__checkout-instance') ?? false;
    if (items.length <= 0 || hasInstance) return;

    const checkout = await fetch('/cart?checkout=Checkout', { method: 'POST' });
    const checkoutInstance = checkout.url;

    Cookies.set('_bl_prod__checkout-instance', checkoutInstance, {
      expires: 21,
    });

    return;
  },
  pushCartToWindow(context) {
    window.dataLayer = window.dataLayer || [];

    const items = context.map((item) => {
      // TODO: in my next PR
      // let bundle = item.components.properties?._product_bundle_variant || null;
      return {
        id: `${item.id}_${item.variant_id}`,
        category: item.product_type,
        product_id: item.id,
        variant_id: item.variant_id,
        title: item.title,
        variant: item.variant_title,
        price: item.price,
        quantity: item.quantity,
        // product_bundle_variant_id: bundle,
        sku: item.sku,
      };
    });

    window.dataLayer.push({
      event: 'cart_contents',
      ecommerce: {
        cartResponse: [...items],
      },
    });
  },
  /**
   * Refreshes cart state and either opens the drawer, closes it, or (for the
   * cart interstitial A/B variant) surfaces the just-added item silently.
   * @param {'open' | 'closed' | 'silent'} mode
   * @param {object|null} formData - the payload just POSTed to /cart/add.js, used in 'silent'
   * mode to identify which cart item (or bundle) to show in the interstitial.
   */
  async displayCart(mode = 'open', formData = null) {
    if (mode === 'closed') return closeCart();

    // Captured before this.items is overwritten below, so the interstitial can
    // diff against it to isolate the quantity just added (see withAddedQuantityOnly).
    const previousItems = this.items;

    const response = await this.fetchEndpoint();
    this.items = response.items;
    this.note = response.note;
    this.subtotal = response.total;
    this.pushCartToWindow(this.items);

    if (mode === 'silent') {
      // Don't let a second add (e.g. the free tote auto-added at a subtotal
      // threshold) replace whichever item the interstitial is already showing.
      if (!this.interstitialItem) {
        const matchedItem = this.findInterstitialItem(formData, previousItems);
        // If the just-added item can't be found (e.g. an unexpected cart response
        // shape), fall back to the cart page rather than adding silently with no
        // confirmation at all.
        if (!matchedItem) return (window.location.href = '/cart');
        this.interstitialItem = this.withAddedQuantityOnly(
          matchedItem,
          previousItems
        );
      }
      // Lets ATC buttons (e.g. ProductActions.vue's loading state) know the
      // interstitial is now showing, mirroring loadCart()'s 'init-overlay' for
      // the flyout cart.
      window.dispatchEvent(new CustomEvent('cart:interstitial:open'));
      return;
    }

    return loadCart();
  },
  /**
   * Returns a shallow copy of the matched cart line (and its bundle components, if any) with
   * `quantity` overridden to reflect only the amount just added, rather than the cumulative
   * amount in that cart line — which also includes any quantity of the same variant/bundle
   * already in the cart before this add. Diffs the freshly matched line against its own
   * pre-add state, matched by `key`, so the interstitial's Price display isn't inflated by
   * pre-existing quantity.
   * @param {object} item - the merged cart line just located via findInterstitialItem
   * @param {Array} previousItems - `this.items` as of just before this add
   * @returns {object}
   */
  withAddedQuantityOnly(item, previousItems) {
    const previousItem = (previousItems ?? []).find(
      (prev) => prev.key === item.key
    );
    const addedQuantity = item.quantity - (previousItem?.quantity ?? 0);

    const components = item.components?.map((component) => {
      const previousComponent = previousItem?.components?.find(
        (prev) => prev.key === component.key
      );
      const addedComponentQuantity =
        component.quantity - (previousComponent?.quantity ?? 0);
      return {
        ...component,
        // Falls back to the full quantity if the diff comes out <= 0 (e.g. an
        // unexpected mismatch), so the interstitial never shows nothing added.
        quantity:
          addedComponentQuantity > 0
            ? addedComponentQuantity
            : component.quantity,
      };
    });

    return {
      ...item,
      quantity: addedQuantity > 0 ? addedQuantity : item.quantity,
      ...(components ? { components } : {}),
    };
  },
  /**
   * Locates the item just added to cart (per the /cart/add.js payload) within the freshly
   * fetched cart items, so the interstitial popup can display it. Bundle adds are POSTed as
   * multiple component line items sharing a `_product_bundle_instance` property, which the
   * cart transform merges into a single `is_bundle` line — match on that shared value.
   *
   * Note: the endpoint's `item.id` is the *product* id, not the variant id (multiple variants
   * of the same product share it) — the variant id only appears as the prefix of `item.key`
   * (e.g. `"43758946386010:..."`), so a single-item match must compare against that instead.
   *
   * Yotpo rewards redemptions add to cart outside our own /cart/add.js call, so there's no
   * form payload to match against — `formData.rewardRedemption` instead flags to find the
   * redeemed item by diffing against `previousItems` (the cart just before the redemption)
   * for a new line carrying Yotpo's loyalty discount property.
   * @param {object|null} formData
   * @param {Array} [previousItems] - `this.items` as of just before this add; only used
   * for `formData.rewardRedemption`
   * @returns {object|null}
   */
  findInterstitialItem(formData, previousItems) {
    if (!formData) return null;
    const items = this.items ?? [];

    if (formData.rewardRedemption) {
      const previousKeys = new Set(
        (previousItems ?? []).map((item) => item.key)
      );
      return (
        items.find(
          (item) =>
            item.properties?._yotpo_loyalty_discount_type &&
            !previousKeys.has(item.key)
        ) ?? null
      );
    }

    if (Array.isArray(formData.items)) {
      const instance = formData.items[0]?.properties?._product_bundle_instance;
      return (
        items.find((item) => {
          if (!item.is_bundle) return false;
          if (item.properties?._product_bundle_instance === instance) {
            return true;
          }
          // The parent's own properties may not carry the instance value —
          // fall back to checking its merged components.
          return (item.components ?? []).some(
            (component) =>
              component.properties?._product_bundle_instance === instance
          );
        }) ?? null
      );
    }

    const variantId = formData.id?.toString();
    return (
      items.find((item) => item.key?.split(':')?.[0] === variantId) ?? null
    );
  },
  closeInterstitial() {
    this.interstitialItem = null;
  },
  cartListeners() {
    // pubSub.publish dispatches a real CustomEvent, so subscribers receive the
    // Event itself — the payload lives at event.detail, not the event object.
    pubSub.subscribe('cart:request', (e) => {
      const { mode, formData } = e?.detail ?? {};
      return this.displayCart(mode, formData);
    });
  },
  async addGiftNote(message = '') {
    const removing = message.length <= 0;
    const quantity = removing ? '0' : '1';
    const properties = { message: message, _freeGift: true };
    const id = this.gift_note.toString();
    const payload =
      {
        id,
        quantity,
        properties,
      } ?? {};

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: `application/json`,
      },
      body: JSON.stringify({ note: message }),
    });

    const createEvent = (item) =>
      new CustomEvent('theme:add-to-cart', {
        detail: item,
      });

    const event = createEvent(payload);

    return removing ? this.fetchEndpoint() : document.dispatchEvent(event);
  },
  /**
   * Fully removes the physical gift note from the cart: clears the stored
   * note text and removes the gift-note product line item. `addGiftNote()`
   * alone only clears the text, so the product line must be removed via a
   * quantity-0 event (same pattern as LineItem.handleChange).
   */
  async removeGiftNote() {
    const noteItem = this.items.find(
      (item) => item.variant_id === this.gift_note
    );
    if (!noteItem) return;

    await this.addGiftNote();

    const event = new CustomEvent('theme:add-to-cart', {
      detail: { id: noteItem.key, quantity: 0 },
    });
    return document.dispatchEvent(event);
  },
  addTote() {
    const { mode, global } = this;
    const tote = global?.tote ?? false;
    if (!tote) return;

    const hasTote = this.totePresentInCart ?? false;
    const toteHasBeenRemoved =
      sessionStorage.getItem('toteManuallyRemoved') ?? false;
    if (toteHasBeenRemoved || hasTote) return;

    const variant = tote.env[mode].variant;
    const quantity = 1;

    const createEvent = (item) =>
      new CustomEvent('theme:add-to-cart', {
        detail: item,
      });

    const payload = {
      id: variant.toString(),
      quantity: quantity,
      properties: { _isGiftCard: false, _freeGift: true },
    };
    const event = createEvent(payload);
    document.dispatchEvent(event);
  },
  removeTote() {
    const { mode, global } = this;
    const tote = global?.tote ?? false;
    if (!tote) return;

    const hasTote = this.totePresentInCart ?? false;
    if (!hasTote) return;
    const variant = tote.env[mode].variant;
    const quantity = 0;

    const createEvent = (item) =>
      new CustomEvent('theme:add-to-cart', {
        detail: item,
      });

    const payload = {
      id: variant.toString(),
      quantity: quantity,
      properties: { _isGiftCard: false, _freeGift: true },
    };
    const event = createEvent(payload);
    document.dispatchEvent(event);
  },
  handleToteInteraction() {
    sessionStorage.setItem('toteManuallyRemoved', true);
  },
  async checkToteStatus() {
    const { global } = this;
    const tote = global?.tote ?? false;

    if (!tote) return (this.global.tote = false);
    const handle = tote?.handle ?? false;

    if (!handle) return (this.global.tote = false);

    const request = {
      variables: { handle },
      query: PRODUCT_RESPONSE_FROM_HANDLE,
    };
    const response = await fetchStorefront(request).then(
      (res) => res.data.product
    );
    if (!response) return (this.global.tote = false);
    return;
  },
  displayGWP() {
    this.gwp.active = true;
  },
  async removeGWP() {
    this.gwp.active = false;
    const cart = await this.fetchEndpoint();
    const items = cart.items;
    const giftWithPurchase = items.find((item) =>
      Object.keys(item.properties).includes('_gift-with-purchase')
    );
    if (giftWithPurchase) {
      const id = giftWithPurchase.key;
      const quantity = 0;

      document.dispatchEvent(
        new CustomEvent('theme:add-to-cart', {
          detail: {
            id,
            quantity,
          },
        })
      );
      this.gwp.added = false;
    }
  },
  gwpAdded() {
    this.gwp.added = true;
    this.gwp.active = false;
  },
};
